<template lang="">
  <b-modal
    id="modal-input-error-messages"
    :title="$t('myAccount.systemConfigs.errorMessages.updateTitle')"
    size="xl"
    :centered="true"
    :scrollable="false"
    :hide-footer="true"
    :no-close-on-backdrop="true"
    :no-close-on-esc="true"
    @show="showHandle"
  >
    <IAmOverlay :loading="loading">
      <ValidationObserver ref="refFormObserver">
        <BTable
          :items="errorListToEdit"
          :fields="columns"
          striped
          hover
          bordered
          small
          responsive
          class="table-sm"
          show-empty
          :empty-text="$t('myAccount.systemConfigs.errorMessages.emptyData')"
        >
          <template #cell(index)="data">
            {{ data.index + 1 }}
          </template>
          <template #cell(key)="data">
            <ValidationProvider
              #default="{ errors }"
              name="key"
              rules="required"
              class="w-100"
            >
              <BFormInput
                :value="data.item.key"
                class="d-none"
              />
              <IAmInputTextOrTextarea
                :id="`key-${data.index}`"
                :text.sync="data.item.key"
                name="key"
                autocomplete="off"
                :lengthBreak="40"
                :state="errors.length > 0 ? false : null"
                :placeholder="$t('myAccount.systemConfigs.errorMessages.keyPlaceholder')"
              />
              <BTooltip
                v-if="errors.length > 0"
                :title="errors[0]"
                variant="danger"
                class="tooltip-error"
                :target="`key-${data.index}`"
              >
                {{ errors[0] }}
              </BTooltip>
            </ValidationProvider>
          </template>
          <template #cell(viValue)="data">
            <ValidationProvider
              #default="{ errors }"
              name="vi"
              rules="required"
              class="w-100"
            >
              <BFormInput
                :value="data.item.viValue"
                class="d-none"
              />
              <IAmInputTextOrTextarea
                :id="`vi-${data.index}`"
                :text.sync="data.item.viValue"
                name="vi"
                autocomplete="off"
                :lengthBreak="40"
                :state="errors.length > 0 ? false : null"
                :placeholder="$t('myAccount.systemConfigs.errorMessages.viPlaceholder')"
              />
              <BTooltip
                v-if="errors.length > 0"
                :title="errors[0]"
                variant="danger"
                class="tooltip-error"
                :target="`vi-${data.index}`"
              >
                {{ errors[0] }}
              </BTooltip>
            </ValidationProvider>
          </template>
          <template #cell(value)="data">
            <ValidationProvider
              #default="{ errors }"
              name="en"
              rules="required"
              class="w-100"
            >
              <BFormInput
                :value="data.item.value"
                class="d-none"
              />
              <IAmInputTextOrTextarea
                :id="`en-${data.index}`"
                :text.sync="data.item.value"
                name="en"
                autocomplete="off"
                :lengthBreak="40"
                :state="errors.length > 0 ? false : null"
                :placeholder="$t('myAccount.systemConfigs.errorMessages.enPlaceholder')"
              />
              <BTooltip
                v-if="errors.length > 0"
                :title="errors[0]"
                variant="danger"
                class="tooltip-error"
                :target="`en-${data.index}`"
              >
                {{ errors[0] }}
              </BTooltip>
            </ValidationProvider>
          </template>

          <template #cell(actions)="data">
            <HStack>
              <BButton
                v-b-tooltip.hover.top.window.dark="$t('myAccount.systemConfigs.errorMessages.deleteRow')"
                variant="flat-danger"
                class="rounded p-50"
                @click="onDeleteRow(data.index)"
              >
                <IAmIcon
                  icon="closeOutline"
                  size="16"
                />
              </BButton>
            </HStack>
          </template>
        </BTable>

        <BRow>
          <BCol
            cols="12"
            md="4"
          >
            <ValidationProvider
              #default="{ errors }"
              name="Api key"
              rules="required"
            >
              <HStack center>
                <BFormInput
                  id="api-key-input"
                  v-model="apiKeys"
                  name="Api key"
                  class="flex-1"
                  :type="isShowApiKeys ? 'text' : 'password'"
                  autocomplete="false"
                  :placeholder="$t('myAccount.systemConfigs.errorMessages.apiKeyInput')"
                  :state="errors.length > 0 ? false : null"
                />
                <BButton
                  variant="flat-primary"
                  class="btn-icon p-50 rounded-circle"
                  @click="isShowApiKeys = !isShowApiKeys"
                >
                  <IAmIcon :icon="isShowApiKeys ? 'eye' :'eyeClose'" />
                </BButton>
                <BTooltip
                  v-if="errors.length > 0"
                  :title="errors[0]"
                  variant="danger"
                  class="tooltip-error"
                  target="api-key-input"
                >
                  {{ errors[0] }}
                </BTooltip>
              </HStack>
            </ValidationProvider>
          </BCol>
          <BCol
            cols="6"
            md="4"
          >
            <HStack
              class="gap-2"
              center
            >
              <BButton
                variant="flat-secondary"
                class="px-75"
                @click="$bvModal.hide('modal-input-error-messages')"
              >
                {{ $t('close') }}
              </BButton>
              <BButton
                variant="info"
                class="px-75"
                @click="onClickUpdate"
              >
                {{ $t('myAccount.systemConfigs.errorMessages.update') }}
              </BButton>
            </HStack>
          </BCol>
          <BCol
            cols="6"
            md="4"
          >
            <HStack end>
              <BButton
                variant="info"
                class="px-75"
                @click="onAddRow"
              >
                {{ $t('myAccount.systemConfigs.errorMessages.addRow') }}
              </BButton>
            </HStack>
          </BCol>
        </BRow>
      </ValidationObserver>
    </IAmOverlay>
  </b-modal>
</template>
<script>
import { computed, ref } from '@vue/composition-api'
import {
  BButton, BCol, BFormInput, BRow, BTable, BTooltip,
} from 'bootstrap-vue'
import { cloneDeep } from 'lodash-es'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import axios from 'axios'

import formValidation from '@/@core/comp-functions/forms/form-validation'
import env from '@/libs/env'
import store from '@/store'
import IAmInputTextOrTextarea from '@/components/IAmInputTextOrTextarea.vue'

import useToast from '@useToast'

export default {
  components: {
    BTable,
    BButton,
    BFormInput,
    BTooltip,
    BCol,
    BRow,
    ValidationObserver,
    ValidationProvider,
    IAmOverlay: () => import('@/components/IAmOverlay.vue'),
    IAmInputTextOrTextarea,
  },
  props: {
    errorList: {
      type: Array,
      default: () => [],
    },
    columns: {
      type: Array,
      default: () => [],
    },
  },
  setup(props, { root, emit }) {
    const { refFormObserver } = formValidation()
    const blankErrorMessage = {
      key: '',
      viValue: '',
      value: '',
    }
    const { feConfigsUrls } = env
    const { toastError, toastSuccess } = useToast()
    const errorListToEdit = ref([])
    const loading = ref(false)

    const LOCAL_STORAGE_KEY_FE_CONFIG_API_KEYS = 'feConfigsApiKeys'
    const apiKeys = ref(localStorage.getItem(LOCAL_STORAGE_KEY_FE_CONFIG_API_KEYS))
    const isShowApiKeys = ref(!apiKeys.value)
    const apiKeysArray = computed(() => {
      if (!apiKeys.value?.length) return []
      return apiKeys.value.split(',')
    })

    async function handleUpdateFeConfigs(payload) {
      if (feConfigsUrls?.length !== 4 && apiKeysArray.value?.length !== 4) {
        toastError('myAccount.systemConfigs.errorMessages.updateError')
        return null
      }

      const promiseArray = feConfigsUrls.map((url, urlIndex) => axios.post(`${url}/fe_configs/category`, payload, {
        headers: {
          'ad-api-key': apiKeysArray.value?.[urlIndex] ?? '',
        },
      }))

      localStorage.setItem(LOCAL_STORAGE_KEY_FE_CONFIG_API_KEYS, apiKeys.value)
      return Promise.all(promiseArray)
    }

    async function onClickUpdate() {
      const isSuccess = await refFormObserver.value.validate()
      if (!isSuccess) { return }
      loading.value = true

      const payload = {
        data: [
          {
            category: 'errorMessage',
            language: 'en',
            data: errorListToEdit.value.map(item => ({
              key: item.key,
              value: item.value,
            })),
          },
          {
            category: 'errorMessage',
            language: 'vi',
            data: errorListToEdit.value.map(item => ({
              key: item.key,
              value: item.viValue,
            })),
          },
        ],
      }

      try {
        const res = await handleUpdateFeConfigs(payload)
        if (res) {
          res.forEach(item => {
            if (item.status !== 200) {
              throw new Error('Update error')
            }
          })
          root.$bvModal.hide('modal-input-error-messages')
          store.dispatch('globalConfig/setGlobalConfig')
          toastSuccess('myAccount.systemConfigs.errorMessages.updateSuccess')
          emit('clearErrorList')
        }
      } catch (error) {
        toastError('myAccount.systemConfigs.errorMessages.updateError')
      } finally {
        loading.value = false
      }
    }

    function onAddRow() {
      errorListToEdit.value.push(cloneDeep(blankErrorMessage))
    }

    function onDeleteRow(index) {
      errorListToEdit.value.splice(index, 1)
    }

    function showHandle() {
      errorListToEdit.value = props.errorList.length ? cloneDeep(props.errorList) : [cloneDeep(blankErrorMessage)]
    }
    return {
      errorListToEdit,
      onClickUpdate,
      onAddRow,
      onDeleteRow,
      refFormObserver,
      showHandle,
      loading,
      apiKeys,
      isShowApiKeys,
    }
  },
}
</script>
<style lang="scss" scoped>
::v-deep .table {
  .width-400 {
    width: 400px !important;
  }
  .width-50 {
    width: 50px !important;
  }
}
</style>
